import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FaEye, FaTimes, FaChevronDown, FaGraduationCap,
  FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle,
  FaBriefcase, FaPhone, FaEnvelope, FaSearch, FaQuoteLeft,
  FaBullseye, FaLightbulb, FaUserCheck, FaClipboardList,
  FaPrint, FaSpinner, FaExclamationTriangle, FaSync,
  FaPaperPlane, FaPlus, FaEdit, FaTrash,
} from 'react-icons/fa';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import SendRecueilModal from '../components/SendRecueilModal';
import CreateSessionModal from '../components/CreateSessionModal';
import { getSessions, getAttentes, deleteSession } from '../components/RecueilApi';

const SB_OPEN = 252, SB_CLOSED = 74, SB_GAP = 12, NB_HEIGHT = 68, NB_TOP = 12;

const PRIORITES_OPTS = [
  'Des échanges de pratiques', 'Un perfectionnement',
  'Des nouveautés sur la fonction', 'Des conseils individualisés', 'Des rappels techniques',
];

const fmtDate = d => new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' });
const fmtTime = d => new Date(d).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });

const Attempt = () => {
  const [sidebarOpen,       setSidebarOpen]       = useState(true);
  const [sessions,          setSessions]          = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showDropdown,      setShowDropdown]      = useState(false);
  const [searchQuery,       setSearchQuery]       = useState('');
  const [ficheSel,          setFicheSel]          = useState(null);
  const [showSendModal,     setShowSendModal]      = useState(false);
  const [showCreateModal,   setShowCreateModal]   = useState(false);
  const [editingSession,    setEditingSession]    = useState(null);
  const [fiches,            setFiches]            = useState([]);
  const [statsData,         setStatsData]         = useState(null);
  const [sessionInfo,       setSessionInfo]       = useState(null);
  const [loadingSessions,   setLoadingSessions]   = useState(true);
  const [loadingFiches,     setLoadingFiches]     = useState(false);
  const [errorSessions,     setErrorSessions]     = useState(null);
  const [errorFiches,       setErrorFiches]       = useState(null);
  const [deletingId,        setDeletingId]        = useState(null);

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const c = isDark ? {
    bg:'#090c16', card:'rgba(255,255,255,0.04)', cardB:'rgba(255,255,255,0.08)',
    text:'#c8d4e0', textDim:'#5a6b82', title:'#eef2f7',
    shadow:'0 4px 24px rgba(0,0,0,0.4)', surface:'rgba(255,255,255,0.04)',
    divider:'rgba(255,255,255,0.07)', rowHover:'rgba(255,255,255,0.03)',
    modalBg:'#0f172a', overlay:'rgba(9,12,22,0.8)', dropBg:'#111827',
    theadBg:'rgba(255,255,255,0.07)', theadText:'rgba(255,255,255,0.45)',
    rowBorder:'rgba(255,255,255,0.06)', blockHd:'rgba(255,255,255,0.03)',
  } : {
    bg:'#f0f3f8', card:'#ffffff', cardB:'rgba(0,0,0,0.07)',
    text:'#4d6070', textDim:'#8fa3b5', title:'#1a2535',
    shadow:'0 2px 12px rgba(0,0,0,0.05)', surface:'rgba(0,0,0,0.03)',
    divider:'rgba(0,0,0,0.07)', rowHover:'#fafbfc',
    modalBg:'#ffffff', overlay:'rgba(15,23,42,0.6)', dropBg:'#ffffff',
    theadBg:'#1a2535', theadText:'rgba(255,255,255,0.5)',
    rowBorder:'rgba(0,0,0,0.055)', blockHd:'#fafbfc',
  };

  //  Charger sessions 
  const loadSessions = useCallback(async () => {
    setLoadingSessions(true); setErrorSessions(null);
    try {
      const data = await getSessions();
      setSessions(data);
      if (data.length > 0 && !selectedSessionId) setSelectedSessionId(data[0].id);
    } catch (err) { setErrorSessions(err.message); }
    finally { setLoadingSessions(false); }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  //  Charger fiches 
  const loadFiches = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setLoadingFiches(true); setErrorFiches(null); setFiches([]);
    try {
      const data = await getAttentes(sessionId);
      setFiches(data.fiches || []);
      setStatsData(data.stats || null);
      setSessionInfo(data.session || null);
    } catch (err) { setErrorFiches(err.message); }
    finally { setLoadingFiches(false); }
  }, []);

  useEffect(() => { if (selectedSessionId) loadFiches(selectedSessionId); }, [selectedSessionId, loadFiches]);

  const session  = useMemo(() => sessions.find(s => s.id === selectedSessionId), [sessions, selectedSessionId]);

  const filtered = useMemo(() => fiches.filter(f => {
    const q = searchQuery.toLowerCase();
    return `${f.nom} ${f.prenom} ${f.entite} ${f.fonction}`.toLowerCase().includes(q);
  }), [fiches, searchQuery]);

  const stats = useMemo(() => statsData ? {
    total:       statsData.total,
    attendus:    session?.participants_attendus || 0,
    dejeForme:   statsData.deja_forme,
    demandePers: statsData.demande_pers,
    avecCas:     statsData.avec_cas,
  } : { total:0, attendus:0, dejeForme:0, demandePers:0, avecCas:0 }, [statsData, session]);

  const initials    = nom => (nom||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
  const COLORS      = ['#198754','#fd7e14','#20c997','#0d6efd','#6f42c1','#dc3545'];
  const avatarColor = i => COLORS[i % COLORS.length];

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Supprimer cette session ? Toutes les fiches associées seront supprimées.')) return;
    setDeletingId(id);
    try {
      await deleteSession(id);
      const updated = sessions.filter(s => s.id !== id);
      setSessions(updated);
      if (selectedSessionId === id) setSelectedSessionId(updated[0]?.id || null);
    } catch (err) { alert(err.message); }
    finally { setDeletingId(null); }
  };

  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;

  const OuiNon = ({ val }) => (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:700,
      color:val?'#198754':'#dc3545', background:val?'rgba(25,135,84,.08)':'rgba(220,53,69,.08)',
      border:`1px solid ${val?'rgba(25,135,84,.2)':'rgba(220,53,69,.2)'}`, padding:'3px 10px', borderRadius:20 }}>
      {val?<FaCheckCircle style={{ fontSize:9 }}/>:<FaTimesCircle style={{ fontSize:9 }}/>}
      {val?'Oui':'Non'}
    </span>
  );

  const RepField = ({ label, children, full=false }) => (
    <div style={{ gridColumn:full?'1/-1':'span 1', display:'flex', flexDirection:'column', gap:5 }}>
      <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.6px', color:c.textDim }}>{label}</div>
      <div style={{ fontSize:13.5, color:c.title, fontWeight:500, lineHeight:1.65 }}>{children}</div>
    </div>
  );

  const Block = ({ color, icon: Icon, title, children }) => (
    <div style={{ border:`1px solid ${c.cardB}`, borderRadius:13, overflow:'hidden', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 16px', borderBottom:`1px solid ${c.divider}`, background:c.blockHd, fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.7px', color:c.textDim, borderLeft:`4px solid ${color}` }}>
        <Icon style={{ color }}/> {title}
      </div>
      <div style={{ padding:16 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ background:c.bg, minHeight:'100vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:'Plus Jakarta Sans',sans-serif}
        .at-main{transition:margin-left .28s cubic-bezier(.4,0,.2,1);min-height:100vh}
        .at-body{padding:22px 24px 48px}
        .at-card{background:${c.card};border:1px solid ${c.cardB};border-radius:14px;box-shadow:${c.shadow}}
        .at-drop-btn{display:flex;align-items:center;gap:12px;padding:14px 18px;border-radius:13px;border:1px solid ${c.cardB};cursor:pointer;background:transparent;width:100%;text-align:left;transition:border-color .2s}
        .at-drop-btn:hover{border-color:#fd7e14}
        .at-dropdown{position:absolute;top:calc(100% + 8px);left:0;right:0;border-radius:13px;border:1px solid ${c.cardB};box-shadow:0 12px 36px rgba(0,0,0,.15);z-index:100;overflow:hidden;animation:dropIn .18s ease}
        @keyframes dropIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}
        .at-drop-item{padding:14px 18px;cursor:pointer;transition:background .15s;border-bottom:1px solid ${c.divider}}
        .at-drop-item:last-child{border-bottom:none}
        .at-drop-item:hover{background:rgba(253,126,20,.06)}
        .at-tbl{width:100%;border-collapse:collapse}
        .at-tbl th{padding:12px 18px;font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;text-align:left;white-space:nowrap}
        .at-tbl td{padding:13px 18px;border-bottom:1px solid ${c.rowBorder};vertical-align:middle}
        .at-trow:last-child td{border-bottom:none}
        .at-trow{transition:background .13s;cursor:pointer}
        .at-trow:hover td{background:${c.rowHover}}
        .at-search{display:flex;align-items:center;gap:10px;border:1.5px solid ${c.cardB};border-radius:10px;padding:0 14px;height:42px;background:${c.card};transition:border-color .2s}
        .at-search:focus-within{border-color:#fd7e14}
        .at-search input{flex:1;border:none;outline:none;font-size:14px;background:transparent;color:${c.title}}
        .at-tag{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:6px;font-size:11.5px;font-weight:600;background:rgba(25,135,84,.08);color:#198754;border:1px solid rgba(25,135,84,.15)}
        .at-overlay{position:fixed;inset:0;backdrop-filter:blur(6px);background:${c.overlay};display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px;animation:fadeIn .2s ease}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .at-modal{border-radius:20px;width:100%;max-width:780px;max-height:94vh;display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(0,0,0,.22);overflow:hidden;background:${c.modalBg};animation:popIn .26s cubic-bezier(.34,1.56,.64,1)}
        @keyframes popIn{from{opacity:0;transform:scale(.93) translateY(20px)}to{opacity:1;transform:none}}
        .at-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;border:none;border-radius:10px;font-size:13.5px;font-weight:600;cursor:pointer;transition:all .2s;font-family:inherit}
        .at-btn:hover{transform:translateY(-1px)}
        .at-btn-ghost{display:inline-flex;align-items:center;gap:7px;padding:9px 16px;background:transparent;border:1.5px solid ${c.cardB};border-radius:10px;font-size:13.5px;font-weight:600;cursor:pointer;color:${c.textDim};font-family:inherit;transition:all .2s}
        .at-btn-ghost:hover{border-color:#fd7e14;color:#fd7e14}
        .at-cx{width:34px;height:34px;border-radius:8px;border:1.5px solid ${c.cardB};background:transparent;display:flex;align-items:center;justify-content:center;font-size:14px;cursor:pointer;color:${c.textDim};transition:all .18s}
        .at-cx:hover{background:#dc3545;color:#fff;border-color:#dc3545}
        .at-modal-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px 20px}
        .at-obj{display:flex;align-items:flex-start;gap:10px;padding:10px 12px;border-radius:9px;border:1px solid ${c.cardB};background:${c.surface};margin-bottom:8px}
        .at-obj:last-child{margin-bottom:0}
        .at-obj-num{width:24px;height:24px;border-radius:6px;background:#198754;color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .spin{animation:sp .8s linear infinite}
        @keyframes sp{to{transform:rotate(360deg)}}
        .badge-statut{font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px}
        @media(max-width:1024px){.at-main{margin-left:0!important}}
        @media(max-width:768px){.at-body{padding:14px 14px 32px}.at-modal-grid{grid-template-columns:1fr}}
      `}</style>

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Recueil des Attentes" adminName="Admin" />

      <main className="at-main" style={{ marginLeft:mainML, paddingTop:mainPT }}>
        <div className="at-body">

          {/*  En-tête ─ */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:22, gap:16, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:13, background:'#fd7e14', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}><FaClipboardList /></div>
              <div>
                <h1 style={{ fontSize:20, fontWeight:700, color:c.title, marginBottom:2 }}>Recueil des Attentes des participants</h1>
                <p style={{ fontSize:13, color:c.textDim }}>Gérez vos sessions et visualisez les fiches soumises</p>
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              {/* Bouton Nouvelle session */}
              <button className="at-btn" style={{ background:'linear-gradient(135deg,#fd7e14,#e8700a)', color:'#fff', boxShadow:'0 6px 20px rgba(253,126,20,.28)' }}
                onClick={() => { setEditingSession(null); setShowCreateModal(true); }}>
                <FaPlus style={{ fontSize:11 }} /> Nouvelle session
              </button>
              {/* Bouton Envoyer liens */}
              {session && (
                <button className="at-btn" style={{ background:'#198754', color:'#fff', boxShadow:'0 6px 20px rgba(25,135,84,.22)' }}
                  onClick={() => setShowSendModal(true)}>
                  <FaPaperPlane style={{ fontSize:11 }} /> Envoyer les liens
                </button>
              )}
              <button className="at-btn-ghost" onClick={() => loadFiches(selectedSessionId)}>
                <FaSync style={{ fontSize:11 }} /> Actualiser
              </button>
            </div>
          </div>

          {/*  Erreur sessions  */}
          {errorSessions && (
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', background:'rgba(220,53,69,.06)', border:'1px solid rgba(220,53,69,.2)', borderRadius:12, color:'#dc3545', fontSize:14, fontWeight:600, marginBottom:18 }}>
              <FaExclamationTriangle /> {errorSessions}
              <button onClick={loadSessions} style={{ marginLeft:'auto', background:'transparent', border:'1.5px solid #dc3545', borderRadius:8, padding:'5px 12px', color:'#dc3545', cursor:'pointer', fontSize:13, fontWeight:700 }}>Réessayer</button>
            </div>
          )}

          {/*  Aucune session ─ */}
          {!loadingSessions && sessions.length === 0 && !errorSessions && (
            <div className="at-card" style={{ padding:'56px 24px', textAlign:'center', marginBottom:20 }}>
              <FaGraduationCap style={{ fontSize:52, color:c.textDim, opacity:.3, display:'block', margin:'0 auto 16px' }} />
              <h3 style={{ fontSize:18, fontWeight:700, color:c.title, marginBottom:8 }}>Aucune session de recueil</h3>
              <p style={{ fontSize:14, color:c.textDim, marginBottom:20 }}>Créez votre première session pour commencer à collecter les attentes.</p>
              <button className="at-btn" style={{ background:'linear-gradient(135deg,#fd7e14,#e8700a)', color:'#fff', margin:'0 auto' }}
                onClick={() => { setEditingSession(null); setShowCreateModal(true); }}>
                <FaPlus style={{ fontSize:11 }} /> Créer une session
              </button>
            </div>
          )}

          {/*  Sélecteur session  */}
          {!loadingSessions && sessions.length > 0 && (
            <div style={{ position:'relative', marginBottom:18 }}>
              <button className="at-drop-btn at-card" onClick={() => setShowDropdown(v => !v)}>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(253,126,20,.1)', color:'#fd7e14', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><FaGraduationCap /></div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.6px', color:c.textDim, marginBottom:2 }}>Session sélectionnée</div>
                  <div style={{ fontSize:14, fontWeight:700, color:c.title }}>{session?.titre || '—'}</div>
                  <div style={{ fontSize:12, color:c.textDim, marginTop:3, display:'flex', gap:14, flexWrap:'wrap' }}>
                    {session?.dates && <span><FaCalendarAlt style={{ marginRight:5, fontSize:10 }}/>{session.dates}</span>}
                    {session?.lieu  && <span><FaMapMarkerAlt style={{ marginRight:5, fontSize:10 }}/>{session.lieu}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:11.5, fontWeight:600, color:'#fd7e14', background:'rgba(253,126,20,.08)', padding:'3px 10px', borderRadius:20, whiteSpace:'nowrap' }}>
                    {stats.total} / {stats.attendus} fiches
                  </span>
                  <FaChevronDown style={{ color:c.textDim, fontSize:12, transition:'transform .2s', transform:showDropdown?'rotate(180deg)':'none' }} />
                </div>
              </button>

              {showDropdown && (
                <div className="at-dropdown" style={{ background:c.dropBg }}>
                  {sessions.map(s => (
                    <div key={s.id} className="at-drop-item"
                      style={{ background: s.id===selectedSessionId ? 'rgba(253,126,20,.06)' : 'transparent' }}
                      onClick={() => { setSelectedSessionId(s.id); setShowDropdown(false); setSearchQuery(''); }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                        <div>
                          <div style={{ fontSize:13.5, fontWeight:600, color:c.title, marginBottom:2 }}>{s.titre}</div>
                          <div style={{ fontSize:12, color:c.textDim }}>{s.dates} · {s.lieu} · {s.nb_fiches} fiche{s.nb_fiches>1?'s':''} / {s.participants_attendus} attendus</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          {/* Badge statut */}
                          <span className="badge-statut" style={{
                            color: s.statut==='active'?'#198754':s.statut==='cloturee'?'#718096':'#fd7e14',
                            background: s.statut==='active'?'rgba(25,135,84,.1)':s.statut==='cloturee'?'rgba(113,128,150,.1)':'rgba(253,126,20,.1)',
                          }}>{s.statut==='active'?'Active':s.statut==='cloturee'?'Clôturée':'Brouillon'}</span>
                          {/* Actions */}
                          <button onClick={e=>{ e.stopPropagation(); setEditingSession(s); setShowDropdown(false); setShowCreateModal(true); }}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:c.textDim, padding:'4px 6px', borderRadius:6 }}>
                            <FaEdit style={{ fontSize:13 }}/>
                          </button>
                          <button onClick={e=>{ e.stopPropagation(); handleDeleteSession(s.id); }}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#dc3545', padding:'4px 6px', borderRadius:6 }}>
                            {deletingId===s.id ? <FaSpinner className="spin" style={{ fontSize:12 }}/> : <FaTrash style={{ fontSize:12 }}/>}
                          </button>
                          {s.id===selectedSessionId && <FaCheckCircle style={{ color:'#fd7e14', flexShrink:0 }}/>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/*  Loader sessions  */}
          {loadingSessions && (
            <div className="at-card" style={{ padding:20, marginBottom:18, display:'flex', alignItems:'center', gap:14 }}>
              <FaSpinner className="spin" style={{ color:'#fd7e14', fontSize:20 }}/>
              <span style={{ color:c.textDim, fontSize:14 }}>Chargement des sessions...</span>
            </div>
          )}

          {/*  Stats  */}
          {!loadingFiches && stats.total > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
              {[
                { label:'Fiches reçues',            val:`${stats.total} / ${stats.attendus}`, color:'#fd7e14', sub:`${Math.round(stats.total/Math.max(stats.attendus,1)*100)}% remplissage`          },
                { label:'Déjà formés sur le thème', val:stats.dejeForme,                       color:'#198754', sub:`${stats.total-stats.dejeForme} première${stats.total-stats.dejeForme>1?'s':''} fois` },
                { label:'Démarche personnelle',     val:stats.demandePers,                     color:'#20c997', sub:'Ont demandé à venir'                                                                 },
                { label:'Cas concrets apportés',    val:stats.avecCas,                         color:'#0d6efd', sub:'Dossiers à traiter'                                                                  },
              ].map((k, i) => (
                <div key={i} className="at-card" style={{ padding:18, position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', right:-14, top:-14, width:60, height:60, borderRadius:'50%', background:k.color, opacity:.07 }}/>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.6px', color:c.textDim, marginBottom:8 }}>{k.label}</div>
                  <div style={{ fontSize:i===0?20:28, fontWeight:800, color:k.color, lineHeight:1, marginBottom:5 }}>{k.val}</div>
                  <div style={{ fontSize:12, color:c.textDim }}>{k.sub}</div>
                </div>
              ))}
            </div>
          )}

          {/*  Recherche  */}
          {!loadingFiches && fiches.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <div className="at-search" style={{ width:320 }}>
                <FaSearch style={{ color:c.textDim, fontSize:13, flexShrink:0 }}/>
                <input placeholder="Rechercher par nom, entité, fonction…" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
              </div>
            </div>
          )}

          {/*  Tableau  */}
          <div className="at-card" style={{ overflow:'hidden' }}>
            {loadingFiches ? (
              <div style={{ padding:'64px 20px', textAlign:'center' }}>
                <FaSpinner className="spin" style={{ fontSize:32, color:'#fd7e14', display:'block', margin:'0 auto 14px' }}/>
                <p style={{ fontSize:14, color:c.textDim, fontWeight:600 }}>Chargement des fiches...</p>
              </div>
            ) : errorFiches ? (
              <div style={{ padding:'48px 20px', textAlign:'center' }}>
                <FaExclamationTriangle style={{ fontSize:36, color:'#dc3545', display:'block', margin:'0 auto 12px' }}/>
                <p style={{ fontSize:14, color:'#dc3545', fontWeight:600, marginBottom:12 }}>{errorFiches}</p>
                <button className="at-btn" style={{ background:'#dc3545', color:'#fff', margin:'0 auto' }} onClick={() => loadFiches(selectedSessionId)}>Réessayer</button>
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table className="at-tbl">
                  <thead>
                    <tr style={{ background:c.theadBg }}>
                      {['Participant','Entité / Fonction','Déjà formé','Démarche','Cas concrets','Priorités','Actions'].map((h,i) => (
                        <th key={i} style={{ color:c.theadText }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length ? filtered.map((f, idx) => (
                      <tr key={f.id} className="at-trow" onClick={() => setFicheSel(f)}>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                            <div style={{ width:38, height:38, borderRadius:10, background:avatarColor(idx), color:'#fff', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                              {initials(`${f.prenom} ${f.nom}`)}
                            </div>
                            <div>
                              <div style={{ fontSize:13.5, fontWeight:600, color:c.title }}>{f.prenom} {f.nom}</div>
                              <div style={{ fontSize:11.5, color:c.textDim, marginTop:1 }}>
                                {fmtDate(f.soumis_le)} à {fmtTime(f.soumis_le)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize:13, fontWeight:600, color:c.title }}>{f.fonction}</div>
                          <div style={{ fontSize:11.5, color:c.textDim, maxWidth:220, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{f.entite}</div>
                        </td>
                        <td><OuiNon val={f.deja_forme_theme}/></td>
                        <td><OuiNon val={f.demande_personnellement}/></td>
                        <td><OuiNon val={f.cas_concrets}/></td>
                        <td>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            {(f.priorites||[]).slice(0,2).map((p,i) => <span key={i} className="at-tag" style={{ fontSize:11, width:'fit-content' }}>{p}</span>)}
                            {(f.priorites||[]).length > 2 && <span style={{ fontSize:11, color:c.textDim }}>+{f.priorites.length-2} autre{f.priorites.length-2>1?'s':''}</span>}
                          </div>
                        </td>
                        <td onClick={e=>e.stopPropagation()}>
                          <button className="at-btn" style={{ background:'#198754', color:'#fff', padding:'7px 14px', fontSize:12.5 }} onClick={() => setFicheSel(f)}>
                            <FaEye style={{ fontSize:11 }}/> Détails
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign:'center', padding:'56px 20px', color:c.textDim }}>
                          <FaClipboardList style={{ fontSize:40, display:'block', margin:'0 auto 12px', opacity:.25 }}/>
                          <p style={{ fontSize:14, fontWeight:600 }}>
                            {!selectedSessionId ? 'Créez ou sélectionnez une session' : searchQuery ? 'Aucun résultat' : 'Aucune fiche reçue pour cette session'}
                          </p>
                          {!searchQuery && selectedSessionId && (
                            <button className="at-btn" style={{ background:'#fd7e14', color:'#fff', margin:'14px auto 0', display:'flex' }} onClick={() => setShowSendModal(true)}>
                              <FaPaperPlane style={{ fontSize:11 }}/> Envoyer les liens
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </main>

      {/*  MODAL FICHE ─ */}
      {ficheSel && (
        <div className="at-overlay" onClick={() => setFicheSel(null)}>
          <div className="at-modal" onClick={e=>e.stopPropagation()}>
            {/* Header modal */}
            <div style={{ padding:'18px 22px', borderBottom:`1px solid ${c.divider}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:c.blockHd, flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:13 }}>
                <div style={{ width:46, height:46, borderRadius:12, background:avatarColor(filtered.findIndex(f=>f.id===ficheSel.id)), color:'#fff', fontSize:15, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {initials(`${ficheSel.prenom} ${ficheSel.nom}`)}
                </div>
                <div>
                  <div style={{ fontSize:16, fontWeight:700, color:c.title }}>{ficheSel.prenom} {ficheSel.nom}</div>
                  <div style={{ fontSize:12.5, color:c.textDim }}>{ficheSel.fonction} · {ficheSel.entite}</div>
                  <div style={{ fontSize:11.5, color:c.textDim, marginTop:2 }}>Soumis le {fmtDate(ficheSel.soumis_le)} à {fmtTime(ficheSel.soumis_le)}</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button className="at-btn-ghost" style={{ padding:'7px 14px', fontSize:12.5 }} onClick={() => window.print()}>
                  <FaPrint style={{ fontSize:11 }}/> Imprimer
                </button>
                <button className="at-cx" onClick={() => setFicheSel(null)}><FaTimes /></button>
              </div>
            </div>

            {/* Corps modal */}
            <div style={{ overflowY:'auto', flex:1, padding:'22px 24px' }}>

              <Block color="#198754" icon={FaUserCheck} title="Profil du participant">
                <div className="at-modal-grid">
                  <RepField label="Nom et Prénoms">{ficheSel.prenom} {ficheSel.nom}</RepField>
                  <RepField label="Fonction">{ficheSel.fonction}</RepField>
                  <RepField label="Entité" full>{ficheSel.entite}</RepField>
                  <RepField label="Formation initiale">{ficheSel.formation_initiale || '—'}</RepField>
                  <RepField label="Téléphone"><span style={{ display:'flex', alignItems:'center', gap:7 }}><FaPhone style={{ fontSize:11, color:c.textDim }}/>{ficheSel.telephone||'—'}</span></RepField>
                  <RepField label="Email"><span style={{ display:'flex', alignItems:'center', gap:7 }}><FaEnvelope style={{ fontSize:11, color:c.textDim }}/>{ficheSel.email}</span></RepField>
                  <RepField label="Missions" full>
                    <div style={{ padding:'10px 14px', background:c.surface, borderRadius:9, border:`1px solid ${c.cardB}`, fontStyle:'italic', lineHeight:1.75 }}>{ficheSel.missions}</div>
                  </RepField>
                </div>
              </Block>

              <Block color="#fd7e14" icon={FaBriefcase} title="Contexte & Parcours">
                <div className="at-modal-grid">
                  <RepField label="Déjà formé sur ce thème ?"><OuiNon val={ficheSel.deja_forme_theme}/></RepField>
                  <RepField label="A demandé personnellement ?"><OuiNon val={ficheSel.demande_personnellement}/></RepField>
                  {ficheSel.deja_forme_theme && ficheSel.formation_precedente && (
                    <RepField label="Formation précédente" full>
                      <div style={{ padding:'10px 14px', background:'rgba(25,135,84,.05)', borderRadius:9, border:'1px solid rgba(25,135,84,.12)' }}>{ficheSel.formation_precedente}</div>
                    </RepField>
                  )}
                  {ficheSel.demande_personnellement && ficheSel.difficultes && (
                    <RepField label="Difficultés rencontrées" full>
                      <div style={{ padding:'10px 14px', background:c.surface, borderRadius:9, border:`1px solid ${c.cardB}`, lineHeight:1.75 }}>
                        <FaQuoteLeft style={{ color:c.textDim, marginRight:6, fontSize:11 }}/>{ficheSel.difficultes}
                      </div>
                    </RepField>
                  )}
                  {!ficheSel.demande_personnellement && ficheSel.conseil_par && (
                    <RepField label="Conseillé par" full>
                      <div style={{ padding:'10px 14px', background:c.surface, borderRadius:9, border:`1px solid ${c.cardB}` }}>{ficheSel.conseil_par}</div>
                    </RepField>
                  )}
                  <RepField label="Objectif">
                    <OuiNon val={ficheSel.objectif_prise_de_poste}/>
                    <span style={{ fontSize:12.5, color:c.textDim, marginLeft:8 }}>{ficheSel.objectif_prise_de_poste?'Prise de poste':'Sensibilisation'}</span>
                  </RepField>
                  <RepField label="Cas concrets"><OuiNon val={ficheSel.cas_concrets}/></RepField>
                </div>
              </Block>

              <Block color="#20c997" icon={FaLightbulb} title="Attentes & Priorités">
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom: ficheSel.autres_priorites ? 14 : 0 }}>
                  {PRIORITES_OPTS.map(opt => {
                    const active = (ficheSel.priorites||[]).includes(opt);
                    return (
                      <span key={opt} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 13px', borderRadius:8, fontSize:13, fontWeight:600,
                        background:active?'rgba(25,135,84,.08)':c.surface, color:active?'#198754':c.textDim,
                        border:`1px solid ${active?'rgba(25,135,84,.2)':c.cardB}` }}>
                        {active?<FaCheckCircle style={{ fontSize:10 }}/>:<FaTimesCircle style={{ fontSize:10, opacity:.35 }}/>}{opt}
                      </span>
                    );
                  })}
                </div>
                {ficheSel.autres_priorites && (
                  <div style={{ marginTop:14 }}>
                    <div style={{ fontSize:10.5, fontWeight:700, textTransform:'uppercase', color:c.textDim, marginBottom:6 }}>Autres priorités</div>
                    <div style={{ padding:'10px 14px', background:c.surface, borderRadius:9, border:`1px solid ${c.cardB}`, fontSize:13.5, color:c.title, lineHeight:1.75 }}>{ficheSel.autres_priorites}</div>
                  </div>
                )}
              </Block>

              <Block color="#0d6efd" icon={FaBullseye} title="3 Objectifs opérationnels">
                {(ficheSel.objectifs_operationnels||[]).map((obj, i) => (
                  <div key={i} className="at-obj">
                    <div className="at-obj-num">{i+1}</div>
                    <div style={{ fontSize:13.5, color:c.title, lineHeight:1.6, paddingTop:2 }}>{obj}</div>
                  </div>
                ))}
              </Block>

              {ficheSel.autres_remarques && (
                <Block color="#8fa3b5" icon={FaQuoteLeft} title="Autres remarques">
                  <div style={{ padding:'12px 16px', background:c.surface, borderRadius:9, border:`1px solid ${c.cardB}`, fontSize:13.5, color:c.text, lineHeight:1.75, fontStyle:'italic' }}>
                    « {ficheSel.autres_remarques} »
                  </div>
                </Block>
              )}
            </div>

            {/* Footer modal */}
            <div style={{ padding:'14px 22px', borderTop:`1px solid ${c.divider}`, display:'flex', justifyContent:'flex-end', background:c.blockHd, flexShrink:0 }}>
              <button className="at-btn-ghost" onClick={() => setFicheSel(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/*  MODAL ENVOI LIENS  */}
      {showSendModal && session && (
        <SendRecueilModal
          session={session}
          onClose={() => setShowSendModal(false)}
          onSuccess={() => { setShowSendModal(false); loadFiches(selectedSessionId); }}
        />
      )}

      {/*  MODAL CRÉER / MODIFIER SESSION  */}
      {showCreateModal && (
        <CreateSessionModal
          session={editingSession}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(newSession) => {
            setShowCreateModal(false);
            loadSessions();
            if (newSession?.id) setSelectedSessionId(newSession.id);
          }}
        />
      )}

    </div>
  );
};

export default Attempt;